import { Injectable } from '@angular/core';
import { jwtDecode } from 'jwt-decode';
import { LocalStorageService } from '../infrastructure/storage/local-storage.service';
import { LoggerService } from './logger.service';

export interface User {
  photo?: string;
  [key: string]: unknown;
}

type JwtToken = {
  user: null | User;
  exp: number;
  fullname: string;
};

const defaultToken: JwtToken = { user: null, exp: 0, fullname: '' };

/**
 * Service for managing JWT token authentication.
 * Handles token storage, retrieval, decoding, and expiration validation.
 */
@Injectable({
  providedIn: 'root',
})
export class JWTTokenService {
  jwtToken = '';
  token: JwtToken = defaultToken;

  jwtDecode = jwtDecode;

  constructor(
    private readonly storage: LocalStorageService,
    private readonly logger: LoggerService,
  ) {}

  setToken(token: string | null): this {
    if (token) {
      this.jwtToken = token;
      this.token = this.decode(token);
      // Cookie is already set by backend - DO NOT store in localStorage
      // This prevents XSS attacks from stealing JWT tokens
    } else {
      // Clear token state (logout)
      this.jwtToken = '';
      this.token = defaultToken;
      // Cookie will be cleared by backend /logout endpoint
    }

    return this;
  }

  decode(token: string): JwtToken {
    try {
      return this.jwtDecode(token);
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
    } catch (_e) {
      this.logger.error('Invalid JWT token format - failed to decode');
      throw new Error('Invalid JWT token format');
    }
  }

  getToken(): string | null {
    if (this.jwtToken) {
      return this.jwtToken;
    }

    // Tokens are now in httpOnly cookies
    // Browser sends them automatically - no need to read from storage
    return null;
  }

  getDecodeToken(): JwtToken {
    if (null === this.jwtToken || '' === this.jwtToken) {
      return defaultToken;
    }

    return this.decode(this.jwtToken);
  }

  getUser(): User | null {
    return this.token?.user;
  }

  getExpiryTime(): number {
    return this.token.exp;
  }

  isTokenExpired(): boolean {
    const expiryTime: number = this.getExpiryTime();

    return Date.now() > expiryTime * 1000;
  }

  clear(): void {
    this.jwtToken = '';
    this.token = defaultToken;
    // Cookies cleared by backend /logout endpoint
  }
}
